import CoachMark from '@@types/CoachMark';

import { CoachMarkName, CoachMarkType, PopperType } from '~/constants';
import { uiState } from '~/stores/UiState';
import { action, computed, observable } from 'mobx';
import { assoc, curry, findIndex, propEq } from 'ramda';

class CoachMarkStore {
  @observable.shallow
  steps: CoachMark[] = [];

  @observable
  complete = {
    [CoachMarkType.Main]: false,
    [CoachMarkType.InviteChannel]: false,
    [CoachMarkType.StatusIcon]: false,
    [CoachMarkType.MakeThread]: false,
    [CoachMarkType.CloseThread]: false,
    [CoachMarkType.ExportTaskWithAI]: false,
  };

  @observable type: CoachMarkType;

  @observable currentIndex = 0;

  coachMarkFetchingPromise: Promise<any> | null = null;

  @observable.ref
  private targets = {
    [CoachMarkName.CreateChannel]: null,
    [CoachMarkName.SearchChannel]: null,
    [CoachMarkName.Invite]: null,
    [CoachMarkName.Collection]: null,
    [CoachMarkName.InviteChannel]: null,
    [CoachMarkName.UserMenuStatusIcon]: null,
    [CoachMarkName.ExportTaskWithAI]: null,
  };

  @observable.shallow
  private highlightTargets = {
    [CoachMarkName.Collection]: null,
  };

  @observable.ref
  highlightBoxStyles: { x: number; y: number; width: number; height: number } | null = null;

  @computed
  get isInCoachMark(): boolean {
    return this.currentStep && this.currentStep.backdrop !== 'none' && uiState.popperMap[PopperType.CoachMark];
  }

  @computed
  get isInInviteChannelCoachMark() {
    return findIndex(propEq('name', CoachMarkName.InviteChannel), this.steps) > -1;
  }

  @computed
  get currentStep() {
    return this.steps[this.currentIndex];
  }

  @computed
  get currentTarget() {
    return this.targets[this.currentStep?.name];
  }

  @computed
  get currentHighlightTarget() {
    return this.highlightTargets[this.currentStep?.name];
  }

  setCoachMarkFetchingPromise = (promise) => {
    this.coachMarkFetchingPromise = promise;
  };

  @action
  setCoachMarkComplete = (complete) => {
    this.complete = complete;
  };

  @action
  setMainCoachMarkComplete = (value) => {
    this.complete[CoachMarkType.Main] = value;
    this.coachMarkFetchingPromise = Promise.resolve(this.complete);
  };

  @action
  setInviteChannelCoachMarkComplete = (value) => {
    this.complete[CoachMarkType.InviteChannel] = value;
    this.coachMarkFetchingPromise = Promise.resolve(this.complete);
  };

  @action
  setStatusIconCoachMarkComplete = (value) => {
    this.complete[CoachMarkType.StatusIcon] = value;
    this.coachMarkFetchingPromise = Promise.resolve(this.complete);
  };

  @action
  setCurrentIndex = (currentCoachMarkIndex) => {
    this.currentIndex = currentCoachMarkIndex;
  };

  @action
  setSteps = (steps: CoachMark[]) => {
    this.steps = steps;
  };

  @action
  setType = (type: CoachMarkType) => {
    this.type = type;
  };

  calcHighlightBoxStyles = (): { x: number; y: number; width: number; height: number } => {
    const node = this.currentHighlightTarget || this.currentTarget;
    if (!node) {
      return null;
    }
    const { x, y, width, height } = node.getBoundingClientRect();
    return { x, y, width, height };
  };

  @action
  setHighlightBoxStyles = (styles: { x: number; y: number; width: number; height: number }) => {
    this.highlightBoxStyles = styles;
  };

  setTarget = curry((name: CoachMarkName, node: HTMLElement | null) => {
    this.targets = assoc(name, node, this.targets);
  });

  getTarget = (name: CoachMarkName) => {
    return this.targets[name];
  };

  setHighlightTarget = curry((name: CoachMarkName, node: HTMLElement | null) => {
    this.highlightTargets[name] = node;
  });

  getHighlightTarget = (name: CoachMarkName) => {
    return this.highlightTargets[name];
  };
}

export default CoachMarkStore;

export const coachMarkStore = new CoachMarkStore();
